import { memo, useCallback, useMemo, useState } from "react";
import type { IB2BTextFieldProps } from "@hopper-b2b/ui";
import { TextField as MuiTextField } from "@material-ui/core";
import { Typography } from "../../Typography";

export type NumberInputFieldProps = Omit<IB2BTextFieldProps, "type">;

export const NumberInputField = memo(
  ({ inputProps, ...rest }: NumberInputFieldProps) => {
    const mergedInputProps = {
      ...inputProps,
      pattern: "[0-9]*",
    };

    return (
      <TextInputField type="tel" inputProps={mergedInputProps} {...rest} />
    );
  }
);

export const TextInputField = memo(
  ({
    value,
    disabled,
    helperText,
    onChange,
    inputProps,
    "aria-label": ariaLabel,
    InputProps = {},
    label,
    onBlur,
    onFocus,
    variant,
    placeholder,
    InputLabelProps,
    ...rest
  }: IB2BTextFieldProps) => {
    const [touched, setTouched] = useState(false);
    const [focus, setFocus] = useState(false);

    const showError = useMemo(
      () => touched && !focus && !!helperText,
      [focus, helperText, touched]
    );

    const handleChange = useCallback(
      (e) => {
        onChange?.(e?.target?.value);
      },
      [onChange]
    );

    const handleBlur = useCallback(
      (e) => {
        onBlur?.(e?.target?.value);
        setFocus(false);
      },
      [onBlur]
    );

    const handleFocus = useCallback(
      (e) => {
        onFocus?.(e?.target?.value);
        setTouched(true);
        setFocus(true);
      },
      [onFocus]
    );

    return (
      <MuiTextField
        variant={variant === "default" ? undefined : variant}
        value={value}
        disabled={disabled}
        error={showError}
        placeholder={placeholder}
        helperText={
          showError && <Typography color="inherit">{helperText}</Typography>
        }
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        label={label}
        inputProps={{
          ...inputProps,
          "aria-label": ariaLabel,
        }}
        InputProps={InputProps}
        InputLabelProps={{
          shrink: !!value || placeholder != null,
          ...InputLabelProps,
        }}
        {...rest}
      />
    );
  }
);
